import {
  Box,
  CircularProgress,
  Divider,
  Flex,
  HStack,
  Stack,
  StackDivider,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from '@chakra-ui/react'
import { Fax, FileDBValue, objectToArray, UserGroup, userGroupToFileIdPrefix } from '@hb/shared'
import { DocumentReference, query, where } from 'firebase/firestore'

import { PopUpMessageContext, ThemeContext, useApp, useAuth } from '@hb/react'
import React, { FC, useCallback, useContext, useMemo, useState } from 'react'
import { FAX_RECEIVED_REF, FAX_SENT_REF } from '../../../../backend/db/collections'
import { combineFilesIntoPdf } from '../../../../backend/functions/other'
import { useProfile } from '../../../../contexts/user/ProfileContext'
import { useConsentFormsStatus } from '../../../../hooks'
import { useQuery } from '../../../../hooks/backend/useQuery'
import { CoverageFilesView } from '../../../Assessments/InsuranceCardsView'
import { ActionButton } from '../../../Buttons'
import { Expandable } from '../../../Expandable'
import { FaxPreview } from '../../../Faxes/FaxView'
import { AssignFileAsConsentFormModal } from '../../../Files/AssignFileAsConsentFormModal'
import { FilesListViewWrapper } from '../../../Files/FilesListViewWrapper'
import { MoveFileModal } from '../../../Files/MoveFileModal'
import { BoxHeader } from '../../../Text/BoxHeader'
import { PatientFiles } from './PatientFiles'
import { RecoverAssessmentFiles } from './RecoverAssessmentFiles'
import { RightUploadFilePopover } from './UploadFilePopover'

const AssessmentFaxes = () => {
  const { assessmentId } = useProfile()
  const sentFaxesQuery = useMemo(
    () => (assessmentId ? query(FAX_SENT_REF, where('assessmentId', '==', assessmentId)) : null),
    [assessmentId],
  )
  const receivedFaxesQuery = useMemo(
    () =>
      assessmentId ? query(FAX_RECEIVED_REF, where('assessmentId', '==', assessmentId)) : null,
    [assessmentId],
  )
  const { data: sentFaxes, loading: loadingSentFaxes } = useQuery<Fax>(sentFaxesQuery)
  const { data: receivedFaxes, loading: loadingReceivedFaxes } = useQuery<Fax>(receivedFaxesQuery)
  const sortedByDate = useMemo(() => {
    const arr = [...objectToArray(sentFaxes ?? {}), ...objectToArray(receivedFaxes ?? {})]
    return arr.sort((a, b) => {
      const aDate = new Date(a.created_at).getTime()
      const bDate = new Date(b.created_at).getTime()
      if (!aDate || !bDate) return 0
      return bDate - aDate
    })
  }, [sentFaxes, receivedFaxes])

  return (
    <Expandable
      initExpanded
      borderRadius={6}
      boxShadow="md"
      px={2}
      py={1}
      bg="white"
      header={() => <BoxHeader>Faxes</BoxHeader>}>
      {loadingReceivedFaxes || loadingSentFaxes ? (
        <HStack>
          <CircularProgress isIndeterminate /> <Text>Loading faxes...</Text>
        </HStack>
      ) : (
        <VStack align="flex-start" py={2}>
          {sortedByDate?.length ? (
            sortedByDate.map(fax => <FaxPreview key={fax.id} fax={fax} />)
          ) : (
            <Text fontStyle="italic" color="gray.600" px={2}>
              No faxes yet
            </Text>
          )}
        </VStack>
      )}
    </Expandable>
  )
}

const AssessmentFilesListView = ({
  docRef,
  files,
  access,
  preview,
  assessmentId,
  AddFileComponent,
  canMerge,
  adminView,
  patientId,
  practiceId,
}: {
  docRef: DocumentReference
  files: Record<string, FileDBValue> | undefined
  AddFileComponent?: FC<{ accessLevel: UserGroup }>
  preview?: boolean
  access: UserGroup
  assessmentId: string
  canMerge?: boolean
  adminView: boolean
  patientId: string
  practiceId?: string | null
}) => {
  const { processResponse } = useContext(PopUpMessageContext)
  const { appName } = useApp()
  const isAdmin = useAuth(s => s.admin)
  const [assignModalFile, setAssignModalFile] = useState<{
    file: FileDBValue
    fileId: string
  } | null>(null)
  const [moveModalFile, setMoveModalFile] = useState<{
    file: FileDBValue
    fileId: string
  } | null>(null)

  // Get consent forms status to check for unsigned forms
  const { consentForms } = useConsentFormsStatus(patientId, null, null)
  const unsignedConsentForms = useMemo(
    () => objectToArray(consentForms ?? {}).filter(form => !form.signedOn),
    [consentForms],
  )

  const onCombineFiles = useCallback(
    async (fileIds: string[]) => {
      if (fileIds.length < 2) {
        processResponse({ error: 'Select at least 2 files to merge' })
        return
      }
      const filePrefix = userGroupToFileIdPrefix(access)
      try {
        await combineFilesIntoPdf({
          assessmentId,
          fileIds: fileIds.map(fId => `${filePrefix}.${fId}`),
          appName,
          fileName: 'Combined Files',
        })
      } catch (err: any) {
        processResponse({ error: err.message })
      }
    },
    [access, assessmentId, appName, processResponse],
  )

  const canEdit = useMemo(() => {
    if (appName === 'providers-app') {
      return access === 'practice'
    }
    return access === 'admin'
  }, [access, appName])

  const getFileHeader = useCallback(
    (fileId: string, file: FileDBValue): React.ReactNode => {
      const buttons: React.ReactNode[] = []

      // Move file button - show for admins
      if (adminView && isAdmin) {
        buttons.push(
          <ActionButton
            key="move"
            size="xs"
            onClick={e => {
              e.stopPropagation()
              setMoveModalFile({ file, fileId })
            }}>
            <Text fontSize="xs">Move File</Text>
          </ActionButton>,
        )
      }

      // Assign as consent form button - only for admins, PDF files, and when unsigned consent forms exist
      if (
        adminView &&
        isAdmin &&
        file.type === 'application/pdf' &&
        unsignedConsentForms.length > 0
      ) {
        buttons.push(
          <ActionButton
            key="assign"
            size="xs"
            onClick={e => {
              e.stopPropagation()
              setAssignModalFile({ file, fileId })
            }}>
            <Text fontSize="xs">Assign as Consent Form</Text>
          </ActionButton>,
        )
      }

      return buttons.length > 0 ? (
        <Flex flexFlow="column" pb={1} gap={1}>
          {buttons}
        </Flex>
      ) : null
    },
    [adminView, isAdmin, unsignedConsentForms.length],
  )

  return (
    <>
      <FilesListViewWrapper
        adminView={adminView}
        docRef={docRef}
        files={files}
        access={access}
        preview={preview}
        AddFileComponent={AddFileComponent}
        canMerge={canMerge}
        canEdit={canEdit}
        onCombineFiles={onCombineFiles}
        getFileHeader={getFileHeader}
      />
      {assignModalFile ? (
        <AssignFileAsConsentFormModal
          isOpen={!!assignModalFile}
          fileId={assignModalFile.fileId}
          onClose={() => setAssignModalFile(null)}
          file={assignModalFile.file}
          patientId={patientId}
          assessmentId={assessmentId}
          accessLevel={access}
        />
      ) : null}
      {moveModalFile ? (
        <MoveFileModal
          isOpen={!!moveModalFile}
          fileId={moveModalFile.fileId}
          onClose={() => setMoveModalFile(null)}
          file={moveModalFile.file}
          sourceType="assessment"
          patientId={patientId}
          sourceAccessLevel={access}
          sourceAssessmentId={assessmentId}
          sourcePracticeId={practiceId ?? null}
        />
      ) : null}
    </>
  )
}

const NoPracticeFiles = ({ preview }: { preview?: boolean }) => (
  <Box borderRadius={preview ? 0 : 6} boxShadow="md" bg="white" w="100%">
    <BoxHeader py={1} pr={2} w="100%">
      <Text fontSize={preview ? 'sm' : 'md'} pl={2}>
        Practice Files
      </Text>
    </BoxHeader>
    <Box py={2} px={3} borderTop="1px solid #cdcdcd" bg="gray.50">
      <Text fontStyle="italic" color="gray.600">
        No practice selected for this pregnancy
      </Text>
    </Box>
  </Box>
)

export const AssessmentFiles = ({ preview }: { preview?: boolean }) => {
  const {
    selectedAssessment: { adminRef, practiceRef, populated: selectedAssessment, ref },
    assessmentId,
    userId,
  } = useProfile()

  const { appName } = useApp()
  const { adminFiles, practiceFiles, files } = selectedAssessment ?? {}

  return (
    <ThemeContext.Provider value={{ placeholderAbove: false }}>
      <Stack divider={preview ? <Divider /> : undefined} w="100%" spacing={preview ? 0 : 4}>
        {preview ? null : <CoverageFilesView adminView />}
        {appName === 'app' && adminRef && assessmentId && userId ? (
          <AssessmentFilesListView
            docRef={adminRef}
            preview={preview}
            assessmentId={assessmentId}
            files={adminFiles}
            access="admin"
            adminView
            canMerge
            patientId={userId}
            practiceId={null}
          />
        ) : null}
        {practiceRef && assessmentId && selectedAssessment?.midwifeId && userId ? (
          <AssessmentFilesListView
            docRef={practiceRef}
            preview={preview}
            assessmentId={assessmentId}
            AddFileComponent={appName === 'providers-app' ? RightUploadFilePopover : undefined}
            files={practiceFiles}
            access="practice"
            adminView
            canMerge
            patientId={userId}
            practiceId={selectedAssessment.midwifeId}
          />
        ) : (
          <NoPracticeFiles preview={preview} />
        )}
        {ref && assessmentId && userId ? (
          <AssessmentFilesListView
            docRef={ref}
            preview={preview}
            assessmentId={assessmentId}
            AddFileComponent={appName === 'providers-app' ? RightUploadFilePopover : undefined}
            files={files}
            access="patient"
            adminView
            canMerge
            patientId={userId}
            practiceId={null}
          />
        ) : null}
        {appName === 'app' && !preview ? (
          <>
            <AssessmentFaxes />
            <RecoverAssessmentFiles />
          </>
        ) : null}
      </Stack>
    </ThemeContext.Provider>
  )
}

export const AssessmentAndPatientFiles = ({ width }: { width: number }) => {
  if (width < 1600) {
    // Use Tabs view for smaller screens
    return (
      <Tabs colorScheme="green" variant="soft-rounded">
        <TabList>
          <Tab>Assessment Files</Tab>
          <Tab>Patient Files</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <AssessmentFiles />
          </TabPanel>
          <TabPanel>
            <PatientFiles />
          </TabPanel>
        </TabPanels>
      </Tabs>
    )
  }

  // Display both side by side for larger screens
  return (
    <HStack gap={2} alignItems="flex-start" divider={<StackDivider />} w="100%">
      <Box flex="1" minW="0">
        <BoxHeader mb={2} color="gray.600">
          Pregnancy Files
        </BoxHeader>
        <AssessmentFiles />
      </Box>
      <Box flex="1" minW="0">
        <BoxHeader mb={2} color="gray.600">
          Patient Files
        </BoxHeader>
        <PatientFiles />
      </Box>
    </HStack>
  )
}
